import { ComponentRef, Injectable } from '@angular/core';
import { ComponentPortal } from '@angular/cdk/portal';
import { BehaviorSubject } from 'rxjs';
import { Leg, RfqLegSettings } from '@approot/models';
import { FormGroup } from '@angular/forms';
import { QuoteScheduleComponent } from './quote-schedule.component';
import { uuid } from '@approot/utilities';

export interface ProjectedData {
  leg: Leg;
  form: FormGroup;
  rfqExecutionSettings: RfqLegSettings;
  requestId: number;
  parentTabIndex: number;
  parentLegIndex: number;
}

interface PortalEntry {
  data: ProjectedData;
  portal?: ComponentPortal<QuoteScheduleComponent>;
  componentRef?: ComponentRef<QuoteScheduleComponent>;
}

@Injectable({ providedIn: 'root' })
export class PortalService {
  private entries = new Map<string, PortalEntry>();

  private count$ = new BehaviorSubject<number>(0);
  public countChanges$ = this.count$.asObservable();

  private portals$ = new BehaviorSubject<
    ComponentPortal<QuoteScheduleComponent>[]
  >([]);
  public readonly portalsChanges$ = this.portals$.asObservable();

  constructor() {
    setInterval(() => {
      console.log(this.entries);
    }, 5000);
  }

  private updatePortalsOutput() {
    this.portals$.next(Array.from(this.entries.values()).map(e => e.portal));
  }

  public createEntry(data: ProjectedData): string | void {
    const entries = this.getAllEntries();
    console.log({ entries });
    console.log();

    // avoid duplicating if a schedule from the leg's parent tab has been opened already
    if (!!entries.find(_ => _.data.parentTabIndex === data.parentTabIndex)) {
      return;
    }
    const id = uuid();
    this.entries.set(id, { data });

    console.log('set entries', id, data);

    this.increaseCount();
    this.updatePortalsOutput();

    return id;
  }

  public getEntry(id: string) {
    return this.entries.get(id);
  }

  public getFirstUnattachedEntry(): [string, PortalEntry] | undefined {
    for (const [id, entry] of this.entries.entries()) {
      if (entry.data && !entry.portal && !entry.componentRef) {
        return [id, entry];
      }
    }
    return undefined;
  }

  public updateEntry(id: string, entry: PortalEntry): void {
    console.log('updating entry...');

    const existingEntry = this.entries.get(id);
    if (!existingEntry) return;

    console.log({ existingEntry });

    this.entries.set(id, entry);
    const newEntry = this.getEntry(id);
    console.log({ newEntry });

    this.setComponentInputs(newEntry.componentRef, newEntry.data);
  }

  public getComponentRef(id: string) {
    return this.entries.get(id)?.componentRef;
  }

  public updateEntryRequestIdByParentTabIndex(
    parentTabIndex: number,
    requestId: number
  ) {
    const entries = this.getAllEntries();
    const entryToUpdate = entries.find(
      _ => _.data.parentTabIndex === parentTabIndex
    );
    if (!entryToUpdate) {
      console.error('Failed finding entry to attach requestId');
    }

    const newEntry: PortalEntry = {
      ...entryToUpdate,
      data: { ...entryToUpdate.data, requestId }
    };
    const newEntryId = this.getIdFromParentTabIndex(
      newEntry.data.parentTabIndex
    );

    console.log('updating entry with id', newEntryId, requestId);

    this.updateEntry(
      this.getIdFromParentTabIndex(newEntry.data.parentTabIndex),
      newEntry
    );
  }

  public removeEntry(id: string): void {
    const entry = this.entries.get(id);
    if (entry?.componentRef) {
      entry.componentRef.destroy();
    }
    if (this.entries.delete(id)) {
      this.decreaseCount();
      this.updatePortalsOutput();
    }
  }

  public getIdFromParentTabIndex(parentTabIndex: number): string {
    return Array.from(this.entries.entries()).find(
      entry => entry[1].data.parentTabIndex === parentTabIndex
    )[0];
  }

  public clearAll(): void {
    this.entries.forEach(entry => {
      entry.componentRef?.destroy();
    });
    this.entries.clear();
    this.count$.next(0);
    this.updatePortalsOutput();
  }

  public getAllEntries(): PortalEntry[] {
    return Array.from(this.entries.values());
  }

  private increaseCount(): void {
    const current = this.count$.getValue();
    console.log('increaseCount to', current + 1);
    this.count$.next(current + 1);
  }

  private decreaseCount(): void {
    const current = this.count$.getValue();
    if (current > 0) {
      this.count$.next(current - 1);
    }
  }

  private setComponentInputs(
    componentRef: ComponentRef<QuoteScheduleComponent>,
    projectedDataItem: ProjectedData
  ) {
    componentRef.instance.additionalColumns =
      projectedDataItem.leg.extraScheduleData[
        projectedDataItem.leg.scheduleOpened
      ]?.columns || [];
    componentRef.instance.legData = projectedDataItem.form;
    componentRef.instance.rfqLegSettings =
      projectedDataItem.rfqExecutionSettings;
    componentRef.instance.leg = projectedDataItem.leg;
    componentRef.instance.requestId = projectedDataItem.requestId;
    componentRef.instance.parentTabIndex = projectedDataItem.parentTabIndex;
    componentRef.instance.parentLegIndex = projectedDataItem.parentLegIndex;
    componentRef.changeDetectorRef.detectChanges();

    componentRef.instance.scheduleOpenChanged.subscribe(() => {
      projectedDataItem.leg.scheduleOpened = null;
      componentRef.instance.isModalOpened =
        !!projectedDataItem.leg.scheduleOpened;
      this.removeEntry(
        this.getIdFromParentTabIndex(projectedDataItem.parentTabIndex)
      );
    });

    componentRef.changeDetectorRef.detectChanges();
    setTimeout(() => {
      componentRef.instance.columns = projectedDataItem.leg.scheduleColumnNames;
      componentRef.changeDetectorRef.detectChanges();
    }, 0);
    setTimeout(() => {
      componentRef.instance.scheduleName = projectedDataItem.leg.scheduleOpened;
      componentRef.changeDetectorRef.detectChanges();
    }, 0);
    setTimeout(() => {
      componentRef.instance.ngOnChanges({
        scheduleName: {
          previousValue: undefined,
          currentValue: projectedDataItem.leg.scheduleOpened,
          firstChange: true,
          isFirstChange: () => true
        }
      });
      componentRef.changeDetectorRef.detectChanges();
    }, 0);

    setTimeout(() => {
      componentRef.instance.isModalOpened =
        !!projectedDataItem.leg.scheduleOpened;
      componentRef.changeDetectorRef.detectChanges();
    }, 0);
  }
}
